/**
 * CE QUE L'API RENVOIE, ÉNUMÉRÉ ICI AUSSI.
 *
 * Ces types recopient les vues du serveur (`enVue`) plutôt que d'importer `@destinia/engine`. Ce
 * n'est pas de la paresse : le front consomme une API, pas un moteur. S'il importait les types du
 * moteur, il finirait par en importer une fonction, puis par recalculer une règle de son côté — et
 * il y aurait deux règles, celle qui est testée et celle qui dérive.
 */

export type Caracteristique = "vie" | "attaque" | "defense" | "agilite" | "chance" | "essence";
export type Archetype = "guerrier" | "roublard" | "mage" | "rodeur" | "clerc" | "erudit";
export type Etat = "saignement" | "poison" | "etourdi" | "aveugle" | "terrifie" | "entrave";
export type Blessure = "jambe_brisee" | "bras_casse" | "oeil_perdu" | "cotes_enfoncees" | "main_ecrasee" | "commotion";
export type Rarete = "commun" | "rare" | "epique" | "legendaire";

export type Stats = Record<Caracteristique, number>;

/**
 * LES AVANTAGES DU §8.1, tels que le serveur les calcule. Le front ne les recalcule JAMAIS — il ne
 * sait même pas qu'il existe des paliers : il lit ce dont ce compte-ci dispose. `null` vaut « sans
 * limite » partout, et jamais `Infinity`, qui ne survit pas à un JSON.
 */
export interface Avantages {
  aventuresParMois: number | null;
  aventuresParJour: number | null;
  herosSimultanes: number | null;
  slotsDeCoffre: number;
  modeChronique: boolean;
  voixParJour: number | null;
  exportCarteHd: boolean;
  classementHebdo: boolean;
}

/**
 * L'ABONNEMENT EST UN ÉTAT DATÉ, pas un mot (§8.1). `palier` dit ce dont on dispose AUJOURD'HUI,
 * `palierInscrit` ce qui est écrit au compte : les deux ne se confondent qu'entre deux échéances,
 * et c'est leur écart qui permet d'écrire « votre abonnement a expiré » au lieu de faire
 * disparaître des avantages sans un mot.
 */
export interface AbonnementEnVue {
  palier: string;
  palierInscrit: string;
  expireLe: string | null;
  echu: boolean;
  fragments: number;
  avantages: Avantages;
}

export interface Compte {
  id: string;
  email: string;
  locale: string;
  contenu: string;
  abonnement: AbonnementEnVue;
  fragments: number;
  inscritLe: string;
}

/* --- L'échoppe, le coffre et la caisse (§8.2) ------------------------------ */

export interface ArticleDeBoutique {
  ref: string;
  categorie: "equipement" | "consommable" | "option" | "confort";
  nom: string;
  /** Ce que ça FAIT. Jamais ce que ça coûte : le prix est à côté. */
  dit: string;
  fragments: number;
  quantite: number;
}

/** Ce qui s'achète en argent réel. Les centimes ne sortent QUE d'ici : un prix n'entre jamais dans
 * une requête, le client ne nomme qu'une clef de produit. */
export interface Vitrine {
  ouvert: boolean;
  abonnements: { produit: string; palier: string; periode: "mois" | "an"; centimes: number }[];
  packs: { produit: string; fragments: number; centimes: number }[];
}

export interface LigneDeCoffre {
  objetId: string;
  quantite: number;
  /** Une option d'aventure : elle se range comme le reste, mais ne se porte pas. */
  option: boolean;
}

export interface CoffreEnVue {
  slots: { pris: number; total: number };
  lignes: LigneDeCoffre[];
}

export interface MouvementDeFragments {
  montant: number;
  cause: string;
  solde: number;
  le: string;
}

export interface CommandeEnVue {
  id: string;
  genre: "abonnement" | "fragments";
  produit: string;
  centimes: number;
  devise: string;
  etat: "ouverte" | "payee" | "abandonnee" | string;
  le: string;
  regleeLe: string | null;
}

export interface Pile {
  objetId: string;
  quantite: number;
}

export interface HerosEnVue {
  id: string;
  nom: string;
  archetype: Archetype;
  status: "alive" | "dead" | "retired" | string;
  stats: Stats;
  statsEffectives: Stats;
  pv: number;
  pvMax: number;
  essence: number;
  essenceMax: number;
  or: number;
  ipe: number;
  palier: string;
  poids: number;
  capacite: number;
  slots: { occupes: number; disponibles: number };
  /**
   * LA SURCHARGE TELLE QUE LE SERVEUR L'ENVOIE (§3.3) — un objet, et non un booléen. Le front la
   * typait `boolean` : en JavaScript un objet est toujours vrai, donc la charge s'affichait en
   * rouge d'un bout à l'autre de la partie, sac vide compris. Une alerte permanente est une alerte
   * qu'on ne lit plus, et c'est exactement ce que le design interdit à la couleur.
   */
  surcharge: { ratio: number; malusAgilite: number; immobile: boolean };
  equipement: Record<string, string | null>;
  sac: Pile[];
  etats: { etat: Etat; toursRestants: number | null; intensite: number }[];
  blessures: Blessure[];
  cicatrices: Caracteristique[];
  /** LES BÉNÉDICTIONS EN COURS (§3.7). La seule chose du jeu qui monte une caractéristique, et elle
   * s'éteint à l'horloge : la fiche doit dire de combien et pour combien de temps, sinon un
   * chiffre change sans qu'on sache pourquoi. */
  benedictions: Benediction[];
  /** LA FLAMME (§3.8), avec ce qu'il lui reste. `null` la plupart du temps : on n'allume pas pour
   * marcher au soleil. */
  lumiere: { objetId: string; nom: string; heuresRestantes: number } | null;
}

export interface Benediction {
  objetId: string;
  nom: string;
  bonus: Partial<Stats>;
  heuresRestantes: number;
}

export interface Objet {
  id: string;
  nom: string;
  emplacement: string | null;
  rarete: Rarete;
  poids: number;
  slots: number;
  bonus?: Partial<Stats>;
  arme?: { dega: string; mod: number; distance: boolean; mains: 1 | 2 };
  contenant?: { slots: number };
  consommable?: { soigne?: string; rendEssence?: number; guerit?: Etat[]; nourrit?: boolean };
  empilable?: boolean;
}

/**
 * CE QUE LE FRONT SAIT D'UN OBJET. Le socle public (`/public/objets`) et la petite table qu'une
 * partie transporte n'ont pas la même forme — l'un donne l'objet entier, l'autre ce qu'il faut pour
 * l'afficher. On les ramène ici à une seule forme, pour que l'interface n'ait qu'un vocabulaire.
 */
/** CE QUE FAIT UN CONSOMMABLE, tel que l'interface le lit. `null` sur un objet qui ne se boit pas :
 * la présence de l'objet est la réponse à « puis-je m'en servir ? », et son contenu à « pour quoi
 * faire ? ». Un simple booléen forçait à écrire « Utiliser » sur une ration comme sur un antidote. */
export interface ConsommableConnu {
  soigne: string | null;
  rendEssence: number | null;
  guerit: Etat[];
  nourrit: boolean;
}

/** CE QUE FAIT UN PARCHEMIN (§3.7), tel que l'interface le lit. Même exigence que le consommable :
 * un bouton « Lire » qui ne dit pas ce qu'on lit est un bouton qu'on presse une fois de trop, sur
 * un objet qui ne se retrouve pas. */
export type ParcheminConnu =
  | { genre: "revelation" }
  | { genre: "cartographie"; rayon: number }
  | { genre: "rebouteux" }
  | { genre: "purge" }
  | { genre: "benediction"; bonus: Partial<Stats>; heures: number }
  | { genre: "repli" };

export interface ObjetConnu {
  id: string;
  nom: string;
  rarete: Rarete;
  poids: number;
  slots: number;
  emplacement: string | null;
  consommable: ConsommableConnu | null;
  parchemin?: ParcheminConnu | null;
  /** Ce que ça éclaire, en heures (§3.8). Le bouton porte son chiffre. */
  lumiere?: { heures: number } | null;
  /** LE SORT QU'IL PORTE (§3.2), avec son coût et ses dégâts : le bouton de combat les affiche. */
  sort?: { id: string; nom: string; cout: number; degats: string | null; etat?: string | null } | null;
  /** Ce qu'une clef ouvre, en toutes lettres (§3.6) : c'est ce qu'on relit quarante nœuds plus
   * tard, devant une porte de bronze. */
  clef?: { ouvre: string } | null;
  outil?: { boussole: boolean } | null;
  contenant: boolean;
}

export interface FicheArchetype {
  id: Archetype;
  nom: string;
  base: Stats;
  regenerationPassive: boolean;
  recuperation: string;
  capacite: { id: string; nom: string; cout: number; usageUnique: boolean; cible: "ennemi" | "soi"; description: string };
}

export interface Bareme {
  pvParPointDeVie: number;
  ddBase: number;
  seuilCritiqueMinimum: number;
  chanceParPointDeCritique: number;
  capaciteBase: number;
  capaciteParAttaque: number;
}

export interface Regles {
  pointsARepartir: number;
  maxParCaracteristique: number;
  paliers: { nom: string; min: number; max: number }[];
  /** Les constantes d'équilibrage, servies par l'API : elles vivent dans `regles.ts` côté moteur,
   * et le front ne les recopie pas — il les affiche. */
  bareme: Bareme;
  /** Ce que chaque action de combat fait, en chiffres — affichés sur les boutons (design §3). Ils
   * viennent d'ici et jamais du front : un « +4 » recopié dans un bouton est un mensonge programmé
   * pour le jour où le banc de mesure changera la constante. */
  combat: { bonusParade: number; bonusPostureEsquive: number; malusAttaquePuissante: number };
  archetypes: FicheArchetype[];
}

export interface AventureEnVue {
  id: string;
  titre: string;
  genre: string;
  ton: string;
  theme: string;
  ipe: { min: number; max: number };
  noeuds: number;
  /** Les actes, avec leur titre : le catalogue en montre le nombre, l'aventure les nomme. */
  actes: { numero: number; titre: string }[];
  catalogue: boolean;
  moderation: string;
  graine: number;
  creeeLe: string;
}

export interface Travail {
  id: string;
  statut: "en_attente" | "en_cours" | "fini" | "echoue" | string;
  etape: string | null;
  progression: number;
  demande: { genre?: string; ton?: string; titre?: string; noeudsCibles?: number };
  graine: number;
  aventureId: string | null;
  erreur: string | null;
  creeLe: string;
  demarreLe: string | null;
  finiLe: string | null;
}

export interface NotificationEnVue {
  id: string;
  sujet: string;
  contenu: Record<string, unknown>;
  lue: boolean;
  creeeLe: string;
}

export interface ChoixEnVue {
  id: string;
  label: string;
  ouvert: boolean;
  verrou: "objet_manquant" | "quete_incomplete" | null;
  jet: { stat: Caracteristique; dc: number } | null;
  exigeUnObjet: string | null;
  coutHeures: number;
  /** LE PANNEAU : où mène le chemin. `lieu` n'est rempli que si l'on quitte l'hexagone — un panneau
   * planté dans la pièce où l'on se trouve ne sert à rien. Jamais le titre du nœud d'arrivée. */
  vers: { lieu: string | null; surPlace: boolean; changeDActe: boolean } | null;
  /** CE QU'UN ÉCHEC COÛTE, sans le chiffrer. La formule de dégâts reste au serveur : on pèse un
   * risque, on ne le calcule pas. */
  echec: { blesse: boolean; etat: Etat | null; ailleurs: boolean; lieu: string | null } | null;
}

export interface NoeudEnVue {
  clef: string;
  titre: string;
  texte: string;
  type: string;
  acte: number;
  hexagone: string | null;
  /** Le nom du lieu et son biome (§4.1). On y est : ce n'est pas un secret, et c'est ce qui permet
   * de dessiner l'environnement quand aucun bandeau n'a été posé (design §3). */
  lieu: string | null;
  biome: string | null;
  ancre: boolean;
  /** UN SANCTUAIRE (§3.4) : le repos y dissipe le poison et le saignement, et un rebouteux referme
   * une blessure contre de l'or. Deux gestes qui n'existent qu'ici. */
  sanctuaire: boolean;
  lumiereRequise: boolean;
  /** CE QUE LE NOIR COÛTE ICI ET MAINTENANT (§3.8). `null` si le lieu est clair, ou si l'on a une
   * flamme : « ce lieu demande de la lumière » est une propriété du monde, ceci est un état. */
  obscurite: { tousJets: number; heuresEnPlus: number } | null;
  /** Le bandeau du nœud, quand il a été dessiné et posé (§6). Presque toujours `null`, et le
   * §design exige que son absence ne laisse aucun trou. */
  illustration: string | null;
  /** CE QU'IL Y A À FOUILLER ICI (§3.6). `contenu` est `null` tant que la cache est fermée : le
   * serveur ne dit pas ce qu'il y a dans un coffre qu'on n'a pas ouvert, et l'interface ne peut donc
   * pas le montrer par accident. */
  caches: CacheEnVue[];
  /** Les lieux ont-ils déjà été fouillés ? L'action existe partout, y compris là où il n'y a rien. */
  lieuxFouilles: boolean;
  choix: ChoixEnVue[];
}

export type EspeceDeCache = "cadavre" | "coffre" | "cachette" | "don" | "sol";

export interface CacheEnVue {
  id: string;
  espece: EspeceDeCache;
  libelle: string;
  ouverte: boolean;
  coutHeures: number;
  dissimulee: boolean;
  /** FERMÉE À CLEF, ET L'ON N'A PAS LA CLEF (§3.6). Elle reste affichée — c'est tout l'objet d'une
   * serrure : on la voit, on la note (§4.2), on revient. */
  verrouillee: boolean;
  /** Le NOM de ce qu'il faut, jamais son identifiant : « il vous manque la clef de bronze ». */
  clefManquante: string | null;
  /** Fermée par une énigme (§3.6) : on l'ouvre en répondant. La question est dans le libellé ; la
   * réponse n'existe pas de ce côté-ci, et c'est le but. */
  enigme: boolean;
  contenu: { objetId: string; quantite: number }[] | null;
  or: number | null;
}

/* --- Les annotations de carte (§4.2) et le calque communautaire (§Q21) ---------------- */

export const MARQUEURS = [
  "monstre",
  "coffre",
  "clef",
  "porte",
  "pnj",
  "danger",
  "ressource",
  "enigme",
  "revisiter"
] as const;
export type Marqueur = (typeof MARQUEURS)[number];

export interface Annotation {
  id: string;
  hexId: string;
  hexClef: string;
  marqueur: Marqueur;
  note: string | null;
  publique: boolean;
  resolue: boolean;
  voix: number;
  /** Le nom du héros qui l'a laissée — sur le calque communautaire seulement. */
  heros?: string;
  /** Ce compte a-t-il déjà donné sa voix ? Sur le calque seulement, pour ne pas la proposer deux fois. */
  votee?: boolean;
  posee: string;
}

/** Les deux mains sur la même carte : la sienne, en encre pleine, et celle des autres, plus pâle. */
export interface LesAnnotations {
  miennes: Annotation[];
  calque: Annotation[];
}

export interface CombattantEnVue {
  id: string;
  nom: string;
  camp: "heros" | "adverse";
  pv: number;
  pvMax: number;
  /** L'ESSENCE DU HÉROS, ET DE LUI SEUL (§3.2). `null` pour l'adversaire, comme ses
   * caractéristiques. C'est la valeur du COMBAT — la fiche, elle, n'est mise à jour qu'au
   * dénouement, et un sort lancé au troisième tour ne s'y lit pas encore. */
  essence: number | null;
  essenceMax: number | null;
  etats: { etat: Etat; toursRestants: number | null }[];
  /** Ce qu'on sait de l'adversaire qu'on affronte : son arme, l'état qu'il inflige en touchant, son
   * portrait s'il a été dessiné. Jamais ses caractéristiques — on les apprend en le frappant. */
  arme: string | null;
  inflige: Etat | null;
  /** L'espèce, clef opaque : elle ensemence l'effigie (design §3) pour qu'une même bête ait la même
   * tête du premier au dernier combat. Elle ne dit rien de plus que le nom. */
  espece: string | null;
  illustration: string | null;
}

export interface CombatEnVue {
  tour: number;
  aVous: boolean;
  fin: { issue: "victoire" | "defaite" | "fuite"; tours: number; or: number } | null;
  combattants: CombattantEnVue[];
}

export interface PartieEnVue {
  id: string;
  statut: "active" | "reprise" | "dead" | "finished" | "abandoned" | string;
  mode: "permadeath" | "chronicle" | "heritage" | string;
  pacte: boolean;
  reprises: number;
  finId: string | null;
  flags: string[];
  /** LE CARNET DE QUÊTES (§4.6). Ce que le serveur envoie est déjà trié : ni le titre des étapes à
   * venir, ni ce qu'elles exigent. Le front n'a rien à filtrer, et ne le pourrait pas honnêtement. */
  quetes: QueteAuCarnet[];
  heros: HerosEnVue;
  horloge: {
    heures: number;
    jour: number;
    heureDuJour: number;
    nuit: boolean;
    faim: number;
    fatigue: number;
    malus: Partial<Stats>;
    /** LA PROCHAINE ÉCHÉANCE (§3.4) et dans combien d'heures elle tombe. `null` quand il n'en
     * reste plus, ou quand l'aventure n'en porte pas. Le titre vient de l'aventure : c'est une
     * citation, il ne se traduit pas (§13.1). */
    echeance: { titre: string; dans: number } | null;
    /** Ce que les échéances tombées ajoutent à chaque déplacement. */
    heuresEnPlus: number;
  };
  noeud: NoeudEnVue | null;
  combat: CombatEnVue | null;
  /** Le nom de ce qu'on porte et de ce qu'on voit — les objets d'une aventure générée ne sont dans
   * aucun catalogue public. La vue ne porte QUE ceux-là : le reste de la table est à trouver. */
  /** LE SEUIL (§3.2) : ce qui attend sur ce nœud, tant qu'on ne l'a pas engagé. `null` presque
   * partout. On y lit ce qu'on voit d'un adversaire à travers une clairière — un nom, une
   * silhouette —, jamais ses caractéristiques : celles-là s'apprennent en le frappant. */
  rencontre: {
    adversaires: { espece: string; nom: string; quantite: number; illustration: string | null }[];
  } | null;
  objets: {
    id: string;
    nom: string;
    rarete: Rarete;
    poids: number;
    slots: number;
    emplacement: string | null;
    consommable: ConsommableConnu | null;
  }[];
  ipe: number;
  palier: string;
}

export interface PartieResumee {
  id: string;
  statut: string;
  mode: string;
  aventure: { id: string; titre: string };
  heros: { id: string; nom: string };
  heure: number;
  finId: string | null;
  commenceeLe: string;
  finieLe: string | null;
}

export type Brouillard = "apercu" | "visite";

export interface HexagoneEnVue {
  clef: string;
  q: number;
  r: number;
  biome: string;
  brouillard: Brouillard;
  /** LE REPÈRE (§4.5) : ce qu'on voit de cette case depuis celle d'à côté. Présent même sur un
   * hexagone seulement APERÇU — c'est là tout son emploi : choisir une direction avant d'y aller. */
  repere?: string | null;
  nom?: string;
  region?: string;
  coutHeures?: number;
  /** Un sanctuaire, et seulement sur une case VISITÉE : on ne devine pas un lieu sûr. */
  sanctuaire?: boolean;
}

export interface CarteEnVue {
  ici: string | null;
  /** Le lieu où l'on se trouve, et son plan quand il en existe un (§6) : la carte dit OÙ l'on est,
   * le plan dit à quoi cela ressemble. */
  lieu: { nom: string | null; region: string; illustration: string | null } | null;
  hexagones: HexagoneEnVue[];
  /** LE CAP (§4.5), et seulement si l'on porte une boussole : une direction parmi six et une
   * distance en cases. Elle ne lève aucun brouillard et ne trace aucun chemin — elle répond à « de
   * quel côté ? », qui est la question de la trentième heure de jeu. */
  boussole: { direction: string; distance: number } | null;
}

/** UNE ÉTAPE, TELLE QUE LE JOUEUR A LE DROIT DE LA LIRE (§4.6) : son titre, et rien de plus. */
export interface EtapeAuCarnet {
  id: string;
  titre: string;
  acte: number;
}

export interface QueteAuCarnet {
  id: string;
  titre: string;
  resume: string;
  principale: boolean;
  /** Celles qui sont derrière. C'est votre propre histoire : la cacher ne protégerait personne. */
  faites: EtapeAuCarnet[];
  enCours: EtapeAuCarnet | null;
  /** COMBIEN IL EN RESTE, ET PAS LESQUELLES : une forme, pas un contenu. */
  restantes: number;
  achevee: boolean;
}

export interface LigneDeJournal {
  /** LE CODE DE LA LIGNE (§13.1) et ses paramètres : la phrase se compose chez le lecteur. `null`
   * pour les lignes écrites avant que le journal ne devienne traduisible — celles-là portent leur
   * phrase française dans `resume`, et on l'affiche telle quelle. */
  code: string | null;
  params: Record<string, string | number | boolean | null>;
  resume: string | null;
  heure: number;
}

export interface DefuntEnVue {
  nom: string;
  archetype: Archetype;
  cause: string | null;
  date: string;
}

/* --- Les événements de combat (§3.2). Structurés, jamais rédigés : le récit se compose ici. --- */

export type EvenementCombat =
  | { type: "combat_ouvert"; ordre: { id: string; nom: string; initiative: number }[] }
  | { type: "tour"; numero: number; acteur: string }
  | { type: "tour_perdu"; acteur: string; cause: Etat }
  | {
      type: "attaque";
      acteur: string;
      cible: string;
      jet: number;
      total: number;
      dd: number;
      touche: boolean;
      critique: boolean;
      echecCritique: boolean;
      degats: number;
      puissante: boolean;
      esquive: { jet: number; total: number; reussie: boolean } | null;
    }
  | { type: "parade"; acteur: string; bonus: number }
  | { type: "posture_esquive"; acteur: string; bonus: number }
  | { type: "capacite"; acteur: string; nom: string; cible?: string; detail: string }
  /** UN SORT LANCÉ (§3.2). Il précède l'événement d'attaque qui porte le jet. */
  | { type: "sort"; acteur: string; nom: string; cible: string; cout: number }
  | { type: "objet_utilise"; acteur: string; objetId: string; detail: string }
  | { type: "dissimulation"; acteur: string; jet: number; dd: number; reussie: boolean }
  | { type: "fuite"; acteur: string; jet: number; dd: number; reussie: boolean }
  | { type: "etat_pose"; cible: string; etat: Etat; tours: number | null }
  | { type: "etat_tick"; cible: string; etat: Etat; degats: number }
  | { type: "etat_dissipe"; cible: string; etat: Etat }
  | { type: "blessure"; cible: string; blessure: Blessure }
  | { type: "chute"; cible: string }
  | { type: "fin"; issue: "victoire" | "defaite" | "fuite"; tours: number; butin: { or: number; objets: string[] } };

export type ActionCombat =
  | { type: "attaquer"; cible: string; reaction?: "esquive" | "aucune" }
  | { type: "attaque_puissante"; cible: string; reaction?: "esquive" | "aucune" }
  | { type: "parer"; reaction?: "esquive" | "aucune" }
  | { type: "esquiver" }
  | { type: "utiliser_objet"; objetId: string; reaction?: "esquive" | "aucune" }
  | { type: "capacite"; cible?: string; reaction?: "esquive" | "aucune" }
  /** LANCER UN SORT (§3.2) : celui que porte ce grimoire-là, sur cette cible-là. */
  | { type: "lancer_sort"; objetId: string; cible: string; reaction?: "esquive" | "aucune" }
  | { type: "se_cacher"; reaction?: "esquive" | "aucune" }
  | { type: "fuir" };

/* --- Le social (§Q21, §4.4, §8.1) ------------------------------------------ */

/** Ce que les autres ont laissé là où l'on est. Ce qui n'y est pas ne s'y trouvera jamais : ni le
 * contenu d'une cache, ni les caractéristiques d'un boss, ni les dégâts qui ont tué (§4.4). */
export interface TraceEnVue {
  espece: "death" | "looted" | "boss_scar" | "note";
  heros: string;
  cause: string | null;
  tours: number | null;
  le: string;
}

/** `null` sous cinq passages : « on ne sait pas » et « personne n'en est revenu » sont deux phrases
 * différentes, et l'écran doit pouvoir les distinguer. */
export interface SurvieEnVue {
  passages: number;
  morts: number;
  pourcent: number;
}

export interface LesTraces {
  ici: string | null;
  traces: TraceEnVue[];
  survie: SurvieEnVue | null;
}

/** Le calque social de la carte : un hexagone visité, ce qui s'y est passé. */
export interface HexagoneFrequente {
  clef: string;
  passages: number;
  morts: number;
  survie: SurvieEnVue | null;
}

export interface PlaceAuClassement {
  heros: string;
  archetype: Archetype | string;
  issue: "terminee" | "morte";
  heures: number;
  reprises: number;
  ou: string | null;
  le: string;
}

export interface LaSemaine {
  semaine: string;
  aventure: { id: string; titre: string; genre: string; noeuds: number } | null;
  /** Le classement se LIT avec un abonnement ; il s'écrit pour tout le monde (§8.1). */
  classementOuvert: boolean;
  classement: PlaceAuClassement[];
  maPlace: PlaceAuClassement | null;
}

/** Le journal publié, tel qu'un inconnu le lit. Un parcours, jamais l'aventure. */
export interface JournalPublie {
  heros: { nom: string; archetype: Archetype | string };
  aventure: string;
  issue: "terminee" | "morte";
  cause: string | null;
  heures: number;
  cicatrices: number;
  commenceLe: string;
  finiLe: string | null;
  journal: LigneDeJournal[];
  carte: HexagoneEnVue[];
}
